import { useCallback, useEffect, useMemo, useState } from "react";
import { router } from "@inertiajs/react";
import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDownIcon, CheckIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/ui/command";
import { csrfFetch } from "@lib/csrfFetch";
import { cn } from "@lib/utils";

// Backs the picker flow: /ads → click Create Ad → pick website → pick page →
// submit → redirect to /campaigns/:uuid/content. The button on /ads doesn't
// link anywhere; it opens this modal in place. That sidesteps "which project?"
// at link time — the user answers that question here.

type WebsiteEntry = {
  id: number;
  uuid: string;
  project_id: number;
  name: string;
  domain: string | null;
  updated_at: string;
};

type PageEntry = {
  id: number;
  route: string | null;
  title: string | null;
  file_path: string;
  full_url: string | null;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ── Data hooks ──────────────────────────────────────────────────────────────
// Keys are stable so reopens hit the cache; staleTime gives us "instant" UX
// while still revalidating in the background after a short window.

const pickerKeys = {
  all: ["campaign-picker"] as const,
  websites: (search: string) => [...pickerKeys.all, "websites", search] as const,
  pages: (websiteId: number) => [...pickerKeys.all, "pages", websiteId] as const,
};

function useWebsitesQuery(search: string, enabled: boolean) {
  return useQuery({
    queryKey: pickerKeys.websites(search),
    queryFn: async ({ signal }) => {
      const q = search.trim() ? `?q=${encodeURIComponent(search.trim())}` : "";
      const response = await csrfFetch(`/api/v1/websites${q}`, { signal });
      if (!response.ok) throw new Error("Couldn't load websites");
      const data = (await response.json()) as { websites: WebsiteEntry[] };
      return data.websites ?? [];
    },
    enabled,
    staleTime: 30_000,
  });
}

function usePagesQuery(websiteId: number | null) {
  return useQuery({
    queryKey: pickerKeys.pages(websiteId ?? 0),
    queryFn: async ({ signal }) => {
      const response = await csrfFetch(`/api/v1/websites/${websiteId}/pages`, { signal });
      if (!response.ok) throw new Error("Couldn't load pages");
      return (await response.json()) as PageEntry[];
    },
    enabled: !!websiteId,
    staleTime: 30_000,
  });
}

// ── Component ───────────────────────────────────────────────────────────────

export default function CreateCampaignModal({ open, onOpenChange }: Props) {
  const [search, setSearch] = useState("");
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<number | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const [websiteOpen, setWebsiteOpen] = useState(false);
  const [pageOpen, setPageOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: websites = [], isLoading: websitesLoading } = useWebsitesQuery(search, open);
  const { data: pages = [], isLoading: pagesLoading } = usePagesQuery(selectedWebsiteId);

  // Reset ephemeral selection state when the modal closes so re-opens start
  // clean. We intentionally keep the react-query cache around — the whole
  // point of the rewrite is that reopens feel instant.
  useEffect(() => {
    if (!open) {
      setSelectedWebsiteId(null);
      setSelectedPageId(null);
      setSearch("");
      setSubmitError(null);
      setWebsiteOpen(false);
      setPageOpen(false);
    }
  }, [open]);

  // Auto-pick the first page as soon as a website is chosen — the common
  // case is "one ad, targeting the index page." User can override.
  useEffect(() => {
    if (!selectedWebsiteId) {
      setSelectedPageId(null);
      return;
    }
    if (pages.length === 0) return;
    setSelectedPageId(pages[0]!.id);
  }, [selectedWebsiteId, pages]);

  const selectedWebsite = useMemo(
    () => websites.find((w) => w.id === selectedWebsiteId) ?? null,
    [websites, selectedWebsiteId]
  );
  const selectedPage = useMemo(
    () => pages.find((p) => p.id === selectedPageId) ?? null,
    [pages, selectedPageId]
  );

  const canSubmit = Boolean(selectedWebsite && selectedPage && !submitting);

  const handleSubmit = useCallback(async () => {
    if (!selectedWebsite || !selectedPage) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await csrfFetch("/api/v1/campaigns", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign: {
            project_id: selectedWebsite.project_id,
            website_id: selectedWebsite.id,
            page_id: selectedPage.id,
            thread_id: crypto.randomUUID(),
          },
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({ errors: ["Unknown error"] }));
        const msg = Array.isArray(body.errors) ? body.errors.join(", ") : "Couldn't create campaign";
        setSubmitError(msg);
        return;
      }

      const campaign = await response.json();
      const campaignUuid = campaign.uuid;
      if (!campaignUuid) {
        setSubmitError("Couldn't resolve campaign for redirect");
        return;
      }
      router.visit(`/campaigns/${campaignUuid}/content`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Network error");
    } finally {
      setSubmitting(false);
    }
  }, [selectedWebsite, selectedPage]);

  const noWebsites = !websitesLoading && websites.length === 0 && !search.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Ad Campaign</DialogTitle>
          <DialogDescription>
            Pick the website and landing page you want to drive traffic to.
          </DialogDescription>
        </DialogHeader>

        {noWebsites ? (
          <EmptyWebsitesState />
        ) : (
          <div className="flex flex-col gap-5 py-2">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-base-500">Website</label>
              <Popover open={websiteOpen} onOpenChange={setWebsiteOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={websiteOpen}
                    className="w-full justify-between font-normal"
                    data-testid="campaign-modal-website-combobox"
                  >
                    {selectedWebsite ? (
                      <span className="flex flex-col items-start">
                        <span>{selectedWebsite.name}</span>
                        {selectedWebsite.domain && (
                          <span className="text-xs text-neutral-500">{selectedWebsite.domain}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-neutral-500">Search your websites...</span>
                    )}
                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0"
                  align="start"
                  style={{ width: "var(--radix-popover-trigger-width)" }}
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search your websites..."
                      value={search}
                      onValueChange={setSearch}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {websitesLoading ? "Loading..." : "No websites match."}
                      </CommandEmpty>
                      {websites.map((w) => (
                        <CommandItem
                          key={w.id}
                          value={String(w.id)}
                          onSelect={() => {
                            setSelectedWebsiteId(w.id);
                            setWebsiteOpen(false);
                          }}
                          data-testid={`campaign-modal-website-option-${w.id}`}
                        >
                          <CheckIcon
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedWebsiteId === w.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="flex flex-col">
                            <span>{w.name}</span>
                            {w.domain && (
                              <span className="text-xs text-neutral-500">{w.domain}</span>
                            )}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-base-500">Landing page</label>
              <Popover open={pageOpen} onOpenChange={setPageOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={pageOpen}
                    disabled={!selectedWebsiteId}
                    className="w-full justify-between font-normal"
                    data-testid="campaign-modal-page-combobox"
                  >
                    {selectedPage ? (
                      <span className="flex flex-col items-start">
                        <span>{selectedPage.title || selectedPage.file_path}</span>
                        <span className="text-xs text-neutral-500">{selectedPage.route}</span>
                      </span>
                    ) : (
                      <span className="text-neutral-500">
                        {!selectedWebsiteId
                          ? "Pick a website first"
                          : pagesLoading
                          ? "Loading pages..."
                          : "Select a landing page"}
                      </span>
                    )}
                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0"
                  align="start"
                  style={{ width: "var(--radix-popover-trigger-width)" }}
                >
                  <Command>
                    <CommandInput placeholder="Filter pages..." />
                    <CommandList>
                      <CommandEmpty>No pages on this website.</CommandEmpty>
                      {pages.map((p) => (
                        <CommandItem
                          key={p.id}
                          value={p.title || p.file_path}
                          onSelect={() => {
                            setSelectedPageId(p.id);
                            setPageOpen(false);
                          }}
                          data-testid={`campaign-modal-page-option-${p.id}`}
                        >
                          <CheckIcon
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedPageId === p.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="flex flex-col">
                            <span>{p.title || p.file_path}</span>
                            <span className="text-xs text-neutral-500">{p.route}</span>
                          </span>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedWebsite && !pagesLoading && pages.length === 0 && (
                <span className="text-xs text-accent-yellow-700">
                  This website doesn't have a landing page ready yet. Add one in the website builder first.
                </span>
              )}
            </div>

            {submitError && (
              <div
                className="text-sm text-red-600 bg-red-50 rounded-md p-3"
                data-testid="campaign-modal-error"
              >
                {submitError}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="campaign-modal-submit"
          >
            {submitting ? "Creating..." : "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyWebsitesState() {
  return (
    <div className="py-8 text-center flex flex-col gap-3 items-center">
      <p className="text-sm text-base-500">You haven't built a website yet.</p>
      <Button onClick={() => router.visit("/projects/new")}>Build a Website</Button>
    </div>
  );
}
